import { useEffect, useRef } from "react";

/**
 * Definição de um atalho de teclado. A mesma lista é usada pelo hook (para
 * registrar os handlers) e pelo ShortcutsHelpModal (para exibir a ajuda),
 * garantindo que a ajuda nunca fique dessincronizada da realidade (DRY).
 */
export interface ShortcutDef {
  /** Combinação: "mod+s", "?", "esc", "mod+enter". `mod` = Ctrl no Win/Linux e ⌘ no Mac. */
  combo: string;
  /** Texto exibido no modal de ajuda. */
  description: string;
  /** Agrupamento no modal de ajuda (ex.: "Geral", "Edição"). */
  group?: string;
  /** Se `true`, dispara mesmo com um campo de texto focado (use só para "mod+s"). */
  allowInInput?: boolean;
  handler: (e: KeyboardEvent) => void;
}

/** Retorna `true` se o alvo do evento é um campo editável (input/textarea/select/contenteditable). */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return !!el.isContentEditable;
}

/** Detecta plataforma Apple para exibir ⌘ em vez de Ctrl na ajuda. */
export function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  const source = `${navigator.platform || ""} ${navigator.userAgent || ""}`;
  return /mac|iphone|ipad|ipod/i.test(source);
}

function matchCombo(combo: string, e: KeyboardEvent): boolean {
  const parts = combo
    .toLowerCase()
    .split("+")
    .map((p) => p.trim());
  const needMod = parts.includes("mod");
  const needShift = parts.includes("shift");
  const key = parts[parts.length - 1];

  const mod = e.metaKey || e.ctrlKey;
  // Exigir exatamente o estado de `mod` do combo (evita que Cmd+? dispare "?").
  if (needMod !== mod) return false;
  if (needShift && !e.shiftKey) return false;

  const eventKey = (e.key || "").toLowerCase();
  if (key === "esc") return eventKey === "escape";
  if (key === "enter") return eventKey === "enter";
  if (key === "?") return e.key === "?";
  return eventKey === key;
}

/**
 * Registra atalhos de teclado globais (um único listener) enquanto o
 * componente estiver montado e `enabled` for `true`. Sempre limpa no unmount.
 */
export function useKeyboardShortcuts(
  shortcuts: ShortcutDef[],
  options?: { enabled?: boolean },
): void {
  const enabled = options?.enabled ?? true;
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      // Ignorar auto-repeat (tecla segurada) para não enfileirar N disparos.
      if (e.repeat) return;

      for (const s of shortcutsRef.current) {
        if (!matchCombo(s.combo, e)) continue;
        // Não disparar dentro de campos de texto, exceto quando permitido (ex.: salvar).
        if (!s.allowInInput && isEditableTarget(e.target)) return;
        s.handler(e);
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
